/*
 * @Description: 客户端日志工具
 * @Author: AI Assistant
 * @Date: 2025-07-15
 */

import { env } from './env';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogMethod = 'log' | 'info' | 'warn' | 'error' | 'debug';

interface LoggerConfig {
  enabled?: boolean;
  level?: LogLevel;
  showTimestamp?: boolean;
  showPagePath?: boolean;
  prefix?: string;
}

class ClientLogger {
  private config: Required<LoggerConfig>;
  private readonly levels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  constructor(config: LoggerConfig = {}) {
    this.config = {
      enabled: env.NEXT_PUBLIC_ENABLE_CLIENT_LOGGER === 'true',
      level: env.NEXT_PUBLIC_LOG_LEVEL as LogLevel,
      showTimestamp: true,
      showPagePath: true,
      prefix: env.NEXT_PUBLIC_LOG_PREFIX,
      ...config,
    };
  }

  /**
   * Debug级别日志
   */
  debug(...args: any[]): void {
    this.executeLog('debug', 'debug', ...args);
  }

  /**
   * Info级别日志
   */
  info(...args: any[]): void {
    this.executeLog('info', 'info', ...args);
  }

  /**
   * Warn级别日志
   */
  warn(...args: any[]): void {
    this.executeLog('warn', 'warn', ...args);
  }

  /**
   * Error级别日志
   */
  error(...args: any[]): void {
    this.executeLog('error', 'error', ...args);
  }

  /**
   * 通用日志方法（兼容console.log）
   */
  log(...args: any[]): void {
    this.executeLog('log', 'info', ...args);
  }

  /**
   * 更新配置
   */
  updateConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * 启用日志
   */
  enable(): void {
    this.config.enabled = true;
  }

  /**
   * 禁用日志
   */
  disable(): void {
    this.config.enabled = false;
  }

  /**
   * 获取当前配置
   */
  getConfig(): LoggerConfig {
    return { ...this.config };
  }

  /**
   * 检查日志是否已启用
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * 设置日志级别
   */
  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  /**
   * 获取当前日志级别
   */
  getLevel(): LogLevel {
    return this.config.level;
  }

  /**
   * 获取当前页面路径
   */
  private getCurrentPagePath(): string {
    if (typeof window === 'undefined') return 'unknown';
    return window.location.pathname;
  }

  /**
   * 格式化日志消息
   */
  private formatMessage(level: LogLevel, pagePath: string, ...args: any[]): any[] {
    const parts: string[] = [];

    // 添加前缀
    if (this.config.prefix) {
      parts.push(this.config.prefix);
    }

    // 添加时间戳
    if (this.config.showTimestamp) {
      const timestamp = new Date().toLocaleTimeString();
      parts.push(`[${timestamp}]`);
    }

    // 添加日志级别
    parts.push(`[${level.toUpperCase()}]`);

    // 添加页面路径
    if (this.config.showPagePath && pagePath !== 'unknown') {
      parts.push(`[${pagePath}]`);
    }

    const prefix = parts.join(' ');

    // 如果第一个参数是字符串，则与前缀合并
    if (args.length > 0 && typeof args[0] === 'string') {
      return [`${prefix} ${args[0]}`, ...args.slice(1)];
    }

    return [prefix, ...args];
  }

  /**
   * 检查是否应该输出日志
   */
  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;
    return this.levels[level] >= this.levels[this.config.level];
  }

  /**
   * 执行日志输出
   */
  private executeLog(method: LogMethod, level: LogLevel, ...args: any[]): void {
    if (!this.shouldLog(level)) return;

    const pagePath = this.getCurrentPagePath();
    const formattedArgs = this.formatMessage(level, pagePath, ...args);

    // 使用console[method]而不是直接使用console.log
    // 这样可以避免eslint警告，因为我们是在工具函数中统一管理

    // eslint-disable-next-line no-console
    console[method](...formattedArgs);
  }
}

// 创建默认实例
const logger = new ClientLogger();

// 导出默认实例和类
export { ClientLogger };
export default logger;

// 导出快捷方法（修复this绑定问题）
export const debug = (...args: any[]) => logger.debug(...args);
export const info = (...args: any[]) => logger.info(...args);
export const warn = (...args: any[]) => logger.warn(...args);
export const error = (...args: any[]) => logger.error(...args);
export const log = (...args: any[]) => logger.log(...args);

// 暴露日志控制方法到window对象
if (typeof window !== 'undefined') {
  (window as any).enableLog = logger.enable.bind(logger);
  (window as any).disableLog = logger.disable.bind(logger);
  (window as any).setLevel = logger.setLevel.bind(logger);
}
